import { useJsApiLoader } from '@react-google-maps/api'
import type { PropsWithChildren } from 'react'
import { createContext, useContext, useMemo } from 'react'
import * as React from 'react'

import config from 'constants/config'

type Libraries = Parameters<typeof useJsApiLoader>[0]['libraries']
export const GOOGLE_PLACE_LIBRARY_OPTIONS: Libraries = ['places', 'geometry', 'marker']

interface GoogleMapsContextProps {
  isLoaded: boolean
}

const GoogleMapsContext = createContext<GoogleMapsContextProps>({
  isLoaded: false,
})

/**
 * @deprecated Prefer new Mapbox-specific contexts/components for new work.
 * This loader remains for legacy Google Maps consumers during migration.
 */
export const useGoogleMapsContext = () => {
  return useContext(GoogleMapsContext)
}

interface EnsureGoogleMapsLoadedProps {
  loadingElement?: React.ReactNode
  children: React.ReactNode
}

/**
 * @deprecated Prefer new Mapbox-specific contexts/components for new work.
 * This compatibility helper remains for legacy Google Maps consumers during migration.
 */
export function EnsureGoogleMapsLoaded({ loadingElement = null, children }: EnsureGoogleMapsLoadedProps) {
  const { isLoaded } = useGoogleMapsContext()
  return <>{isLoaded ? children : loadingElement}</>
}

/**
 * @deprecated Prefer new Mapbox-specific contexts/components for new work.
 * This provider remains for legacy Google Maps consumers during migration.
 */
export function GoogleMapsProvider(props: PropsWithChildren) {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    libraries: GOOGLE_PLACE_LIBRARY_OPTIONS,
    googleMapsApiKey: config.GOOGLE_MAPS_API_KEY,
  })

  const value = useMemo<GoogleMapsContextProps>(() => ({
    isLoaded,
  }), [isLoaded])

  return (
    <GoogleMapsContext.Provider value={value}>
      {props.children}
    </GoogleMapsContext.Provider>
  )
}
